/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { localSort } from 'app/shared/lib/sorting';
import { CaseService } from 'app/cm/services/case-service';
import * as CM from 'app/cm/models/essette-api';

export interface CustomTaskQueueHistory extends CM.TaskQueueHistory {
    delegatedTo?: string | null;
}

@autoinject()
export class CaseTasksQueueHistoryGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected taskId: string | null = null;
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected caseId: string | null = null;

    protected queueHistory: CustomTaskQueueHistory[] = [];

    private apiSorting = new Sorting();
    private localSorting: Sorting = new Sorting();
    protected resultCount = 0;
    protected pageSize = 10;

    private localSortProperties: string[] = ['delegatedTo'];

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CaseService,
        ...rest: any[]) {
        super(...rest);

        // Default Sort
        this.apiSorting.entries = [
            {
                reference: 'task.description',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        //If LocalSort
        if(this.localSortProperties.indexOf(property) > -1) {
            const { localSorting } = this;
            const queueHistory: CustomTaskQueueHistory[] = <CustomTaskQueueHistory[]>this.queueHistory;

            if(direction === null) {
                localSorting.remove(property);
            } else {
                localSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            localSort(queueHistory, localSorting);
            if(!this.localSorting.entries.length) this.reload();
        }
        // Else If API Sort
        else {
            const { apiSorting: sorting } = this;
            if(direction === null) {
                sorting.remove(property);
            } else {
                sorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            this.reload();
        }
    }

    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        if(this.taskId)
            await this.loadTaskQueueHistory();
        else if(this.caseId)
            await this.loadCaseTaskQueueHistory();
    }

    private restructureTaskQueueHistory(): void {
        if(!this.queueHistory.length) return;

        this.queueHistory.forEach(history => history.delegatedTo = history.toUser ? history.toUser.description : history.toProvider?.description);
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadTaskQueueHistory(): Promise<void> {
        if(!this.taskId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.apiSorting
        };

        const includes = [
            'metadata.ended.byUser',
            'metadata.started.byUser',
            'queue',
            'toProvider',
            'toUser'
        ];

        const fields = [
            'metadata.ended.byUser.description',
            'metadata.ended.timestamp',
            'metadata.started.byUser.description',
            'metadata.started.timestamp',
            'queue.description',
            'toProvider.description',
            'toUser.description'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadQueueHistoryByTask(this.taskId, options, includes, fields);
            this.queueHistory = response.results;
            this.restructureTaskQueueHistory();
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async loadCaseTaskQueueHistory(): Promise<void> {
        if(!this.caseId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.apiSorting,
            includeInactive: true
        };

        const includes = [
            'metadata.ended.byUser',
            'metadata.started.byUser',
            'queue',
            'task',
            'toProvider',
            'toUser'
        ];

        const fields = [
            'metadata.ended.byUser.description',
            'metadata.ended.timestamp',
            'metadata.started.byUser.description',
            'metadata.started.timestamp',
            'queue.description',
            'task.description',
            'toProvider.description',
            'toUser.description'
        ];

        try {
            this.state.beginLoad();

            const response = await this.service.loadTaskQueueHistoryByCaseId(this.caseId, options, includes, fields);
            this.queueHistory = response.results;
            this.restructureTaskQueueHistory();
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
